import { createContext, useEffect, useRef, useState, useMemo } from 'react';
import { useHistory, useParams, Redirect } from 'react-router-dom';

import { useGet, usePost, useAuth } from 'hooks';
import styled from 'styled-components';

import Profile from './Profile';
import {
  Container,
  Column,
  LeftColumn,
  Pane,
  ContentPane,
  ResultsTitle,
  Row,
  ProfileContainer,
} from './styled';
import { TabbedPanel, Icon } from 'components/common';
import { Loader } from '../common';
import OrganisationProfileFilters from './Filters';
import OrganisationResearchersSearchArea from './Search';
import { getOrgListsForUser, getSuperOrganisation } from 'services/api';
import OrganisationResearchers from './Researchers';
import OrganisationTradeshows from './Tradeshows';
import PurchasingSearchArea from './Purchasing/SearchArea';
import PurchasingNotice from './Purchasing/PurchasingNotice';
import PurchasingResults from './Purchasing/Results';
import OrganisationClinicalTrials from './ClinicalTrials';

import Statistics from './Statistics';
import ResearcherStatistics from './Statistics/ResearcherStatistics';
import OrgResultsHeader from './OrgResultsHeader';
import OrgStaleResultsAlert from './OrgStaleResultsAlert';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearPurchasingFilters,
  performSearch,
  performPurchasingSearch,
  setOrgResearchersSearchRequest,
  setPurchasingSearchRequest,
  setView,
  setPurchasingFilter,
  setOrgWorkItemsSearchRequest,
  performWorkItemSearch,
  setTradeshowsSearchRequest,
  performTradeshowSearch,
  resetAllFilters,
  performClinicalTrialsSearch,
  setClinicalTrialsSearchRequest,
  cancelSearch,
  applyAiFilters,
} from 'actions/organisation';
import {
  getOrganisationView,
  getPurchasingFilters,
  getPurchasingSearchRequest,
  getPurchasingLoading,
  getOrganisationPeopleStale,
  getOrganisationWorkItemsStale,
  getFilter as getOrgFilter,
} from 'selectors/organisation';
import OrganisationItems from './Items';
import { useCallback } from 'react';
import OrganisationWorkItemSearchArea from './Search/ItemSearch';
import useLog, { outcomes } from 'hooks/useLog';
import OrganisationFundingRounds from './Funding';
import CrunchbaseAttribution from './Funding/attribution';
import OrganisationAcquisitions from './Acquisitions';
import {
  ORGANISATIONACQUISITIONSVIEW,
  ORGANISATIONPEOPLEVIEW,
  ORGANISATIONPURCHASINGVIEW,
  ORGANISATIONTRADESHOWSVIEW,
  ORGANISATIONVCFUNDINGVIEW,
  ORGANISATIONWORKITEMVIEW,
  ORGANISATIONINVESTMENTSVIEW,
  ORGANISATIONCLINICALTRIALSVIEW,
  ORGANISATIONSUBSIDIARIESVIEW,
} from './helpers';
import OrganisationDownloadButton from './Downloads';
import Lists from 'components/Researcher/Lists';
import { views } from 'components/Lists/constants';
import { setModal } from 'actions/lists';
import { Create } from 'components/common/Lists/Modals';
import { getActiveModal } from 'selectors/lists';
import { TITLE_POPUP_CREATE_LIST } from 'components/common/labelConstants';
import useAddMembersAndRefetch from 'components/Lists/hooks/useAddMembersAndRefetch';
import OrganisationInvestments from './Investments';
import DefunctNotice from './DefunctNotice';
import CTA from './CTA';
import OrganisationClinicalTrialSearchArea from './Search/ClinicalTrialSearch';
import OrganisationSubsidiaries from './Subsidiaries';
import licenseType from 'models/licenseType';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import PartnerNotice from './PartnerNotice';
import { SciAIOrgSearch } from './SciAIOrgSearch';
import { removeQueryParam } from 'utils/url';
import {
  getFilters as getSearchFilters,
  getSearchView,
} from 'selectors/search';
import {
  CARRY_FILTERS_PARAM,
  mapSearchFiltersToOrgSlice,
} from './carryoverFilters';
import { isReturnToOrgEntry, stampOrgEntry } from './orgEntry';

export const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px 7px;
  ${p => p.theme.breakpoint.tablet} {
    padding: 0px;
  }
`;

const metricActions = {
  [ORGANISATIONPEOPLEVIEW]: 'VisitPeopleTab',
  [ORGANISATIONWORKITEMVIEW]: 'VisitActivityTab',
  [ORGANISATIONTRADESHOWSVIEW]: 'VisitTradeshowsTab',
  [ORGANISATIONPURCHASINGVIEW]: 'VisitPurchasingTab',
  [ORGANISATIONVCFUNDINGVIEW]: 'VisitVcFundingTab',
  [ORGANISATIONACQUISITIONSVIEW]: 'VisitAcquisitionsTab',
  [ORGANISATIONINVESTMENTSVIEW]: 'VisitInvestmentsTab',
  [ORGANISATIONCLINICALTRIALSVIEW]: 'VisitClinicalTrialsTab',
  [ORGANISATIONSUBSIDIARIESVIEW]: 'VisitSubsidiariesTab',
};

const modals = {
  create: Create,
};

export const OrgSearchContext = createContext();
export const OrgPurchasingSearchContext = createContext();

const Organisation = () => {
  const { id } = useParams();
  const location = useLocation();
  const { avoidReset } = location.state || {};
  const idRef = useRef(id);
  const parentRef = useRef(null);
  const dispatch = useDispatch();
  const activeTab = useSelector(getOrganisationView);
  const peopleStale = useSelector(getOrganisationPeopleStale(id));
  const workItemsStale = useSelector(getOrganisationWorkItemsStale(id));
  const fromFollowedLink = useSelector(getOrgFilter(id, 'fromRecentItems'));
  const purchasingFilters = useSelector(getPurchasingFilters);
  const purchasingSearchRequest = useSelector(getPurchasingSearchRequest(id));
  const purchasingLoading = useSelector(getPurchasingLoading);
  const activeModal = useSelector(getActiveModal);
  const searchFilters = useSelector(getSearchFilters);
  const searchView = useSelector(getSearchView);
  const { hasLicense, user } = useAuth();
  const hasSciSearchAI = Boolean(user?.hasSciSearchAI);

  const hasIndustryOnlyLicense =
    hasLicense([licenseType.industry], true) &&
    !hasLicense([licenseType.academic], true);

  const hasAcademicOnlyLicense =
    hasLicense([licenseType.academic], true) &&
    !hasLicense([licenseType.industry], true);

  const [{ res: superOrg, loading }, fetchSuperOrg] = useGet({
    url: getSuperOrganisation(id),
  });

  const [
    { res: editableListsResponse, loading: loadingLists },
    fetchLists,
  ] = useGet({
    url: getOrgListsForUser,
    onMount: true,
    query: { onlyEditable: true },
  });

  const [, handleAddMembers, list] = useAddMembersAndRefetch(
    null,
    fetchLists,
    views.organisations,
    true
  );

  const listOptions = useMemo(
    () =>
      editableListsResponse?.map(l => ({
        label: l.name,
        value: l.id,
        description: l.description,
      })),
    [editableListsResponse]
  );

  const [searchState, postSearch] = usePost({});
  const [tradeshowSearchState, postTradeshowSearch] = usePost({});
  const [purchasingSearchState, postPurchasingSearch] = usePost({});
  const [workItemSearchState, postWorkItemSearchState] = usePost({});
  const [clinicalTrialsSearchState, postClinicalTrialsSearch] = usePost({});

  const searchLoading =
    searchState.loading ||
    purchasingSearchState.loading ||
    workItemSearchState.loading ||
    tradeshowSearchState.loading ||
    clinicalTrialsSearchState.loading;

  const searchParams = new URLSearchParams(
    new URL(window.location.href)?.search
  );
  const initialView = searchParams?.get('initialView');
  const linkNavigation = useRef(null);
  const history = useHistory();
  // Whether this org page has been on the current history entry before — a
  // Back, Forward or reload — rather than arriving at a new one. Fixed at mount
  // (per org id): the stamp below changes location state straight after.
  const returningRef = useRef(null);

  const [getIsReset, setIsReset] = useState(false);
  const [peopleInitialLoad, setPeopleInitialLoad] = useState(true);
  const [workItemsInitialLoad, setWorkItemsInitialLoad] = useState(true);
  const [tradeshowsInitialLoad, setTradeshowsInitialLoad] = useState(true);
  const [purchasingInitialLoad, setPurchasingInitialLoad] = useState(true);
  const [clinicalTrialsInitialLoad, setClinicalTrialsInitialLoad] = useState(
    true
  );

  // A new history entry starts clean; one this org page has already been on
  // keeps its filters. Reads the entry's state off history at the moment the
  // effect runs, then stamps the entry so coming back to it is recognised.
  useEffect(() => {
    const returning = isReturnToOrgEntry(history.location.state, id);
    returningRef.current = returning;

    if (!avoidReset && !returning) {
      dispatch(resetAllFilters(id));
      // Only the Followed Organisations link makes the reset pick Last month.
      setIsReset(Boolean(fromFollowedLink));
    }
    if (!returning) {
      history.replace(stampOrgEntry(history.location, id));
    }
    fetchSuperOrg();
    setPeopleInitialLoad(true);
    setWorkItemsInitialLoad(true);
    setTradeshowsInitialLoad(true);
    setPurchasingInitialLoad(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fetchSuperOrg, dispatch, avoidReset]);

  // Declared after the reset effect above, which is what lets the carried filters
  // survive it. Only a new entry is honoured: a reload or Back landing on an entry
  // that still has the param would otherwise re-apply the search over edits the
  // user has since made here. No search is run — the initial-load effects below
  // wait on superOrg and will pick the filters up, so the tab still searches
  // exactly once.
  useEffect(() => {
    if (avoidReset || returningRef.current) return;
    if (searchParams?.get(CARRY_FILTERS_PARAM) !== 'true') return;

    const { filters } = mapSearchFiltersToOrgSlice(searchFilters, searchView);

    if (Object.keys(filters).length > 0) {
      dispatch(applyAiFilters(id, filters));
    }

    // Spread the location so the entry stamp in its state survives.
    history.replace({
      ...history.location,
      search: removeQueryParam(history.location.search, CARRY_FILTERS_PARAM),
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (superOrg && !superOrg.redirectId) {
      document.title = `${superOrg.organisationName} | SciLeads`;

      if (Object.keys(config).includes(initialView)) {
        linkNavigation.current = true;
        dispatch(setView(initialView));
        history.replace({
          ...history.location,
          search: removeQueryParam(history.location.search, 'initialView'),
        });
      } else if (!Object.keys(config).includes(activeTab)) {
        dispatch(setView(ORGANISATIONPEOPLEVIEW));
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superOrg]);

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  useEffect(() => {
    if (searchState.res || searchState.error || searchState.loading) {
      dispatch(setOrgResearchersSearchRequest(idRef.current, searchState));
    }
  }, [searchState, dispatch]);

  useEffect(() => {
    if (
      purchasingSearchState.res ||
      purchasingSearchState.error ||
      purchasingSearchState.loading
    ) {
      dispatch(
        setPurchasingSearchRequest(idRef.current, purchasingSearchState)
      );
    }
  }, [purchasingSearchState, dispatch]);

  useEffect(() => {
    if (
      workItemSearchState.res ||
      workItemSearchState.error ||
      workItemSearchState.loading
    ) {
      dispatch(
        setOrgWorkItemsSearchRequest(idRef.current, workItemSearchState)
      );
    }
  }, [workItemSearchState, dispatch]);

  useEffect(() => {
    if (
      tradeshowSearchState.res ||
      tradeshowSearchState.error ||
      tradeshowSearchState.loading
    ) {
      dispatch(setTradeshowsSearchRequest(idRef.current, tradeshowSearchState));
    }
  }, [tradeshowSearchState, dispatch]);

  useEffect(() => {
    if (
      clinicalTrialsSearchState.res ||
      clinicalTrialsSearchState.error ||
      clinicalTrialsSearchState.loading
    ) {
      dispatch(setClinicalTrialsSearchRequest(id, clinicalTrialsSearchState));
    }
  }, [clinicalTrialsSearchState, dispatch, id]);

  const showLoader = loading || !superOrg;

  const handleSearch = () => {
    dispatch(performSearch(postSearch, id));
  };

  const handleWorkItemSearch = useCallback(() => {
    dispatch(performWorkItemSearch(postWorkItemSearchState, id));
  }, [dispatch, postWorkItemSearchState, id]);

  // SCIL-17744: People and Activity share filters. A filter change flags both
  // tabs; running a tab's search clears only its own flag. Activating a tab
  // whose flag is still set auto-runs its search; otherwise nothing dispatches.
  // Marking the initial load done stops the initial-load effect firing a
  // second, duplicate search right after the auto-run.
  const handleTabChange = value => {
    dispatch(setView(value));
    if (value === ORGANISATIONPEOPLEVIEW && peopleStale) {
      handleSearch();
      setPeopleInitialLoad(false);
    } else if (value === ORGANISATIONWORKITEMVIEW && workItemsStale) {
      handleWorkItemSearch();
      setWorkItemsInitialLoad(false);
    }
  };

  // SciAI confirm: the AI has just restaged both tabs, so switch to the one its
  // classification picked and run it unconditionally. Marking that tab's initial
  // load done first stops the initial-load effect firing a duplicate search.
  const handleAiSearchOnTab = tab => {
    // The AI set Date, not the Followed Organisations link.
    setIsReset(false);
    if (tab === ORGANISATIONWORKITEMVIEW) {
      setWorkItemsInitialLoad(false);
      dispatch(setView(tab));
      handleWorkItemSearch();
    } else {
      setPeopleInitialLoad(false);
      dispatch(setView(tab));
      handleSearch();
    }
  };

  const handleCreateNewList = () => {
    setTimeout(() => dispatch(setModal('create')), 400);
  };

  const handleCreateListModalClose = options => {
    if (options?.id && activeModal === 'create') {
      handleAddMembers(options, id);
    }

    dispatch(setModal(''));
  };

  const getSponsoredTrialCount = superOrg => {
    if (hasIndustryOnlyLicense) return superOrg?.industrySponsoredTrials;

    if (hasAcademicOnlyLicense) return superOrg?.academicSponsoredTrials;

    return superOrg?.sponsoredTrials;
  };

  const handleClinicalTrialSearch = useCallback(() => {
    dispatch(
      performClinicalTrialsSearch(postClinicalTrialsSearch, {
        organisationId: id,
        organisationName: superOrg.organisationName,
      })
    );
  }, [dispatch, postClinicalTrialsSearch, id, superOrg]);

  const handleCancel = () => {
    dispatch(cancelSearch(id));
  };

  //initial load
  useEffect(() => {
    if (!superOrg || initialView || !peopleInitialLoad) return;

    if (activeTab === ORGANISATIONPEOPLEVIEW) {
      dispatch(performSearch(postSearch, id));
      setPeopleInitialLoad(false);
    }
  }, [
    dispatch,
    superOrg,
    id,
    postSearch,
    initialView,
    peopleInitialLoad,
    activeTab,
  ]);

  useEffect(() => {
    if (!superOrg || initialView || !workItemsInitialLoad) return;

    if (activeTab === ORGANISATIONWORKITEMVIEW) {
      handleWorkItemSearch();
      setWorkItemsInitialLoad(false);
    }
  }, [
    dispatch,
    superOrg,
    handleWorkItemSearch,
    initialView,
    activeTab,
    workItemsInitialLoad,
  ]);

  useEffect(() => {
    if (!superOrg || initialView || !tradeshowsInitialLoad) return;

    if (activeTab === ORGANISATIONTRADESHOWSVIEW) {
      dispatch(performTradeshowSearch(postTradeshowSearch, id));
      setTradeshowsInitialLoad(false);
    }
  }, [
    activeTab,
    dispatch,
    id,
    initialView,
    postTradeshowSearch,
    superOrg,
    tradeshowsInitialLoad,
  ]);

  useEffect(() => {
    if (!superOrg || initialView || !clinicalTrialsInitialLoad) return;

    if (activeTab === ORGANISATIONCLINICALTRIALSVIEW) {
      dispatch(
        performClinicalTrialsSearch(postClinicalTrialsSearch, {
          organisationId: id,
          organisationName: superOrg.organisationName,
          newSearch: true,
        })
      );
      setClinicalTrialsInitialLoad(false);
    }
  }, [
    activeTab,
    dispatch,
    id,
    initialView,
    postClinicalTrialsSearch,
    superOrg,
    clinicalTrialsInitialLoad,
  ]);

  useEffect(() => {
    if (!superOrg || initialView || !purchasingInitialLoad) return;

    if (activeTab === ORGANISATIONPURCHASINGVIEW) {
      const restoredFilters =
        avoidReset &&
        purchasingFilters &&
        Object.keys(purchasingFilters).length > 0;

      if (!restoredFilters) {
        dispatch(clearPurchasingFilters);
      }
      dispatch(
        setPurchasingFilter('supplierName', [superOrg.organisationName])
      );
      dispatch(
        performPurchasingSearch(postPurchasingSearch, {
          organisationId: id,
          newSearch: true,
        })
      );
      setPurchasingInitialLoad(false);
    }
  }, [
    activeTab,
    dispatch,
    id,
    initialView,
    postPurchasingSearch,
    purchasingInitialLoad,
    purchasingLoading,
    purchasingSearchRequest,
    purchasingFilters,
    superOrg,
    avoidReset,
  ]);

  const postLog = useLog(null, null, null, false);
  useEffect(() => {
    if (activeTab && !initialView) {
      postLog({
        description: JSON.stringify({
          entityId: id,
          location: linkNavigation.current ? 'Link' : 'Navigation',
        }),
        action: metricActions[activeTab],
        outcome: outcomes.success,
      });
      linkNavigation.current = false;
    }
  }, [activeTab, id, initialView, postLog]);

  if (superOrg?.redirectId) {
    return <Redirect to={`/organisation/${superOrg.redirectId}`} />;
  }

  const config = {};

  // SCIL-18091: searchable tabs end their pane at the search bar; the stat
  // cards float on the page background and the results get their own pane,
  // mirroring the main search page.
  const searchResults = {};

  // The specialised tabs have no search bar, so their pane ends at the tab bar
  // and the body drops down here, leaving the stat cards between the two. Each
  // Statistic is itself a white Pane, so the cards need the page background
  // behind them rather than the inside of a pane.
  const tabBodies = {};

  // SCIL-17604: a sparkle on People/Activity signals SciSearch AI can drive those
  // tabs. Rendered only for licensed users, matching the entry button gate.
  const sparkle = hasSciSearchAI ? (
    <Icon
      icon='sparkles'
      color={null}
      style={{ marginLeft: 4, width: 12, height: 12 }}
    />
  ) : null;

  config[ORGANISATIONPEOPLEVIEW] = {
    icon: 'users',
    text: 'People',
    adornment: sparkle,
    content: (
      <TableContainer>
        <OrganisationResearchersSearchArea
          organisation={superOrg}
          handleSearch={handleSearch}
          handleCancel={handleCancel}
          loading={peopleInitialLoad}
        />
      </TableContainer>
    ),
  };
  searchResults[ORGANISATIONPEOPLEVIEW] = (
    <TableContainer>
      <OrganisationResearchers
        organisation={superOrg}
        handleSearch={handleSearch}
        loading={peopleInitialLoad}
      />
    </TableContainer>
  );
  config.workItems = {
    icon: 'folderOpen',
    text: 'Activity',
    adornment: sparkle,
    content: (
      <TableContainer>
        <OrganisationWorkItemSearchArea
          organisation={superOrg}
          handleSearch={handleWorkItemSearch}
          handleCancel={handleCancel}
          loading={workItemsInitialLoad}
        />
      </TableContainer>
    ),
  };
  searchResults.workItems = (
    <TableContainer>
      <OrganisationItems
        organisation={superOrg}
        handleSearch={handleWorkItemSearch}
        loading={workItemsInitialLoad}
        parentRef={parentRef}
      />
    </TableContainer>
  );
  config[ORGANISATIONTRADESHOWSVIEW] = {
    icon: 'calendar',
    text: 'Tradeshows',
    content: (
      <OrganisationTradeshows
        organisation={superOrg}
        loading={tradeshowsInitialLoad}
      />
    ),
  };

  if (
    getSponsoredTrialCount(superOrg) > 0 ||
    superOrg?.totalClinicalTrials > 0
  ) {
    config[ORGANISATIONCLINICALTRIALSVIEW] = {
      icon: 'trial',
      text: 'Clinical Trials',
      content: (
        <TableContainer>
          <OrganisationClinicalTrialSearchArea
            organisation={superOrg}
            handleSearch={handleClinicalTrialSearch}
            handleCancel={handleCancel}
            loading={clinicalTrialsInitialLoad}
          />
        </TableContainer>
      ),
    };
    searchResults[ORGANISATIONCLINICALTRIALSVIEW] = (
      <TableContainer>
        <OrganisationClinicalTrials
          organisation={superOrg}
          loading={clinicalTrialsInitialLoad}
          handleSearch={handleClinicalTrialSearch}
        />
      </TableContainer>
    );
  }

  if (superOrg?.totalPurchasing > 0) {
    config[ORGANISATIONPURCHASINGVIEW] = {
      icon: 'searchDollar',
      text: 'Products Sold',
      content: (
        <PurchasingSearchArea
          organisation={superOrg}
          handleCancel={handleCancel}
        />
      ),
    };
    tabBodies[ORGANISATIONPURCHASINGVIEW] = (
      <>
        <PurchasingNotice />
        <PurchasingResults
          organisation={superOrg}
          loading={purchasingInitialLoad}
        />
      </>
    );
  }

  if (superOrg?.superOrganisationFundingRounds?.length) {
    config[ORGANISATIONVCFUNDINGVIEW] = {
      icon: 'sackDollar',
      text: 'VC Funding',
    };
    tabBodies[ORGANISATIONVCFUNDINGVIEW] = (
      <OrganisationFundingRounds organisation={superOrg} />
    );
  }

  if (superOrg?.superOrganisationAcquisitions?.length) {
    config[ORGANISATIONACQUISITIONSVIEW] = {
      icon: 'handShake',
      text: 'Acquisitions',
    };
    tabBodies[ORGANISATIONACQUISITIONSVIEW] = (
      <OrganisationAcquisitions organisation={superOrg} />
    );
  }

  if (superOrg?.superOrganisationInvestments?.length) {
    config[ORGANISATIONINVESTMENTSVIEW] = {
      icon: 'dollarNote',
      text: 'Investments',
    };
    tabBodies[ORGANISATIONINVESTMENTSVIEW] = (
      <OrganisationInvestments organisation={superOrg} />
    );
  }

  if (superOrg?.superOrganisationSubsidiaries) {
    config[ORGANISATIONSUBSIDIARIESVIEW] = {
      icon: 'networkWired',
      text: 'Subsidiaries',
    };
    tabBodies[ORGANISATIONSUBSIDIARIESVIEW] = (
      <OrganisationSubsidiaries organisation={superOrg} />
    );
  }

  if (showLoader) {
    return <Loader />;
  }

  const ModalCreateNewList = modals[activeModal];
  return superOrg.showCta ? (
    <CTA></CTA>
  ) : (
    <>
      <OrgSearchContext.Provider value={postSearch}>
        <OrgPurchasingSearchContext.Provider value={postPurchasingSearch}>
          <Container>
            <Row alignTop>
              <LeftColumn>
                <Pane>
                  <ProfileContainer>
                    <Profile loading={showLoader} organisation={superOrg} />
                  </ProfileContainer>
                </Pane>
                <SciAIOrgSearch
                  organisationId={id}
                  organisationName={superOrg.organisationName}
                  activeTab={activeTab}
                  onRunSearchOnTab={handleAiSearchOnTab}
                />
                <OrganisationProfileFilters
                  organisation={superOrg}
                  onFilterUpdate={handleSearch}
                  onworkItemFilterUpdate={handleWorkItemSearch}
                  onClinicalTrialFilterUpdate={handleClinicalTrialSearch}
                  handleCancel={handleCancel}
                />
                <Pane title='List Management' style={{ marginTop: 15 }}>
                  <Lists
                    editableLists={listOptions}
                    disabled={searchLoading}
                    loadingLists={loadingLists}
                    type={views.organisations}
                    onCreateNewItemList={handleCreateNewList}
                    newListCreatedId={list?.id}
                    organisationName={superOrg.organisationName}
                  />
                </Pane>
              </LeftColumn>
              <Column ref={parentRef}>
                <DefunctNotice organisation={superOrg} />
                <ContentPane
                  condensed={Boolean(
                    searchResults[activeTab] || tabBodies[activeTab]
                  )}
                >
                  <PartnerNotice />
                  <TabbedPanel
                    horizontal
                    activeTab={activeTab}
                    onActiveTabChange={handleTabChange}
                    config={config}
                    leftMargin
                  />
                </ContentPane>
                <Statistics organisation={superOrg} loading={showLoader} />
                {tabBodies[activeTab] && (
                  <ContentPane
                    title={config[activeTab].text}
                    headerRight={
                      <OrganisationDownloadButton organisation={superOrg} />
                    }
                  >
                    {tabBodies[activeTab]}
                  </ContentPane>
                )}
                {searchResults[activeTab] && (
                  <>
                    <ResearcherStatistics
                      organisation={superOrg}
                      loading={showLoader}
                    />
                    <ContentPane
                      title={
                        <ResultsTitle>
                          <span>Search Results</span>
                          <OrgStaleResultsAlert organisationId={superOrg.id} />
                        </ResultsTitle>
                      }
                      headerRight={
                        <OrganisationDownloadButton organisation={superOrg} />
                      }
                    >
                      <OrgResultsHeader
                        organisation={superOrg}
                        activeTab={activeTab}
                        onPeopleSearch={handleSearch}
                        onWorkItemSearch={handleWorkItemSearch}
                        isReset={getIsReset}
                        onResetConsumed={() => setIsReset(false)}
                      />
                      {searchResults[activeTab]}
                    </ContentPane>
                  </>
                )}
                <CrunchbaseAttribution activeTab={activeTab} />
              </Column>
            </Row>
            {activeModal && (
              <ModalCreateNewList
                title={TITLE_POPUP_CREATE_LIST}
                buttonOkText='Create and Add'
                onClose={handleCreateListModalClose}
                type={views.organisations}
              />
            )}
          </Container>
        </OrgPurchasingSearchContext.Provider>
      </OrgSearchContext.Provider>
    </>
  );
};

export const getLinkedInNetwork = () => {
  return {
    type: 'linkedin',
    displayName: 'LinkedIn',
  };
};

export default Organisation;
